/**
 * Custom Format Schedule Generator
 *
 * A static, upfront schedule generator driven by simple counts — number of
 * rounds, games per round, and playoff games — rather than a fixed algorithm
 * like round robin or single elimination. Lets an organizer generate a full
 * round/match skeleton without creating each round and match by hand.
 *
 * With exactly 2 teams every match is trivially Team A vs Team B. With 3+
 * teams, "games per team" caps how many times any *specific* pairing can
 * repeat — the fill picks the least-used pairing for each slot, skipping any
 * pairing already at the cap in favor of one that isn't, only reusing a
 * capped pairing if every pairing is already capped and slots remain.
 */

const generateMatchId = () => `match-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const pairKey = (i, j) => `${i}-${j}`;

/**
 * Assigns each team to a group. Teams with an explicit assignment (via
 * `manualAssignments`, keyed by participant id) keep it; everyone else is
 * distributed to whichever group currently has the fewest teams, so group
 * sizes stay as even as possible whether or not any teams were pinned by
 * hand. Returns an array parallel to `teams`: groupIndex per team.
 */
const assignGroups = (teams, numberOfGroups, manualAssignments = null) => {
  const assignment = new Array(teams.length).fill(null);
  const unassignedIndices = [];

  teams.forEach((team, idx) => {
    const manual = manualAssignments?.[team.id];
    if (manual != null && manual >= 0 && manual < numberOfGroups) {
      assignment[idx] = manual;
    } else {
      unassignedIndices.push(idx);
    }
  });

  const groupCounts = new Array(numberOfGroups).fill(0);
  assignment.forEach(g => { if (g != null) groupCounts[g]++; });

  unassignedIndices.forEach(idx => {
    let smallest = 0;
    for (let g = 1; g < numberOfGroups; g++) {
      if (groupCounts[g] < groupCounts[smallest]) smallest = g;
    }
    assignment[idx] = smallest;
    groupCounts[smallest]++;
  });

  return assignment;
};

/**
 * Builds `numberOfRounds` rounds of `gamesPerRound` games each.
 *
 * For 2 teams: every slot is the same single pairing (grouping doesn't apply).
 * For 3+ teams: within each round, greedily fills each slot with the
 * least-used *eligible* pairing (ties broken randomly for variety), skipping
 * any pairing already at `gamesPerTeamCap` in favor of one that isn't; once
 * every eligible pairing is at the cap, falls back to least-used overall so
 * the requested total is still met.
 *
 * A team can appear in a round at most `teamCapacity` times before it's
 * excluded from further games *in that same round*. `teamCapacity` is how
 * many of a team's players can be on court at once, divided by however many
 * actually play one game — an 8-player doubles roster (2 play a game) can
 * field 4 different pairs on 4 different courts simultaneously, so its
 * capacity is 4, not 1. For sports where the whole roster plays as a single
 * unit (no per-match player subset), capacity is 1 — a team genuinely can't
 * play two matches at once there. `gamesPerRound` is validated up front
 * against `maxSimultaneous` (every team's capacity, summed and halved), the
 * most simultaneous games any round can hold before some team runs out of
 * available players/pairs.
 *
 * `groupAssignment` (parallel array of group index per team) + `groupMode`
 * ('within' or 'across') restrict which pairings are eligible at all:
 * 'within' only allows same-group pairs, 'across' only allows different-group
 * pairs. Omit both for no grouping (every pairing eligible).
 */
const buildRounds = (teams, numberOfRounds, gamesPerRound, gamesPerTeamCap, groupAssignment = null, groupMode = null, teamCapacity = 1) => {
  const n = teams.length;
  const capacityOf = () => teamCapacity; // uniform across teams — the UI has one Players per Team field, not per-team

  if (n === 2) {
    const maxSimultaneous = Math.min(capacityOf(), capacityOf());
    const hasRoundConflicts = gamesPerRound > maxSimultaneous;
    const rounds = Array.from({ length: numberOfRounds }, () =>
      Array.from({ length: gamesPerRound }, () => ({ teamA: teams[0], teamB: teams[1] }))
    );
    return { rounds, hasRoundConflicts, maxSimultaneous };
  }

  let allPairs = [];
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      allPairs.push([i, j]);
    }
  }

  if (groupAssignment && groupMode) {
    allPairs = allPairs.filter(([i, j]) => {
      const sameGroup = groupAssignment[i] === groupAssignment[j];
      return groupMode === 'within' ? sameGroup : !sameGroup;
    });
  }

  if (allPairs.length === 0) {
    throw new Error(
      groupMode === 'within'
        ? 'No eligible pairings — at least one group needs 2+ teams for within-group play'
        : groupMode === 'across'
          ? 'No eligible pairings — need at least 2 groups for cross-group play'
          : 'No eligible pairings available'
    );
  }

  // Every team has the same capacity (uniform Players per Team input), so the
  // most simultaneous games any round can hold is every team's capacity,
  // summed and halved (each game consumes one capacity-unit from each of its
  // two teams). Rather than blocking generation outright (a round might
  // legitimately need more than that, just not conflict-free), we allow it
  // but flag it so the caller can surface a non-blocking warning.
  const maxSimultaneous = Math.floor((n * teamCapacity) / 2);
  let hasRoundConflicts = false;

  const usage = new Map(allPairs.map(p => [pairKey(...p), 0]));
  const rounds = [];

  for (let r = 0; r < numberOfRounds; r++) {
    const usedThisRound = new Map(); // team idx -> times already placed this round
    const roundPairs = [];

    for (let g = 0; g < gamesPerRound; g++) {
      const cap = Number(gamesPerTeamCap) || Infinity;
      const underCapacity = ([i, j]) =>
        (usedThisRound.get(i) || 0) < teamCapacity && (usedThisRound.get(j) || 0) < teamCapacity;

      let eligible = allPairs.filter(p => usage.get(pairKey(...p)) < cap && underCapacity(p));
      if (eligible.length === 0) eligible = allPairs.filter(underCapacity); // every pairing capped — must reuse to hit the total
      if (eligible.length === 0) {
        // Every team already at capacity this round (gamesPerRound >
        // maxSimultaneous) — no way to avoid exceeding it, so allow any
        // pairing and flag the conflict.
        eligible = allPairs.filter(p => usage.get(pairKey(...p)) < cap);
        if (eligible.length === 0) eligible = allPairs;
        hasRoundConflicts = true;
      }

      const shuffled = shuffleArray(eligible);
      shuffled.sort((a, b) => usage.get(pairKey(...a)) - usage.get(pairKey(...b)));
      const [i, j] = shuffled[0];
      usage.set(pairKey(i, j), usage.get(pairKey(i, j)) + 1);
      usedThisRound.set(i, (usedThisRound.get(i) || 0) + 1);
      usedThisRound.set(j, (usedThisRound.get(j) || 0) + 1);
      roundPairs.push({ teamA: teams[i], teamB: teams[j] });
    }

    rounds.push(roundPairs);
  }

  return { rounds, hasRoundConflicts, maxSimultaneous };
};

/**
 * CUSTOM FORMAT GENERATOR
 *
 * Generates `numberOfRounds` league rounds of `gamesPerRound` matches each
 * (total volume = numberOfRounds × gamesPerRound, fixed), followed by a
 * single Playoffs round of `numberOfPlayoffGames` placeholder matches —
 * teams TBD, resolved later from standings, same pattern as League + Finals.
 */
export const generateCustomFormat = (participants, options = {}) => {
  if (!participants || participants.length < 2) {
    throw new Error('At least 2 participants are required');
  }

  // How many of a team's own players can be on court at once — an 8-player
  // doubles roster (2 play a game) fields 4 simultaneous pairs, so its
  // capacity is 4, not 1. Team sports with no per-match player subset
  // (requiredPlayerCount not given) get capacity 1: the whole roster plays
  // as one unit, so a team genuinely can't be in two games at once.
  const requiredPlayerCount = Number(options.requiredPlayerCount) || null;
  const teamCapacity = requiredPlayerCount && options.playersPerTeam
    ? Math.max(1, Math.floor(Number(options.playersPerTeam) / requiredPlayerCount))
    : 1;

  // Coerce every count with Number(...) || fallback rather than a plain
  // destructuring default — a blank/NaN input (e.g. Generate clicked before
  // the field blurs) resolves to a usable number instead of silently
  // producing a 0-round schedule (NaN comparisons are always false, so a
  // bare `numberOfRounds < 1` guard wouldn't catch it).
  const numberOfRounds = Number(options.numberOfRounds) || 1;
  const gamesPerRound = Number(options.gamesPerRound) || Math.max(1, Math.floor((participants.length * teamCapacity) / 2));
  const numberOfPlayoffGames = Number.isFinite(Number(options.numberOfPlayoffGames)) ? Number(options.numberOfPlayoffGames) || 0 : 0;
  const gamesPerTeam = Number(options.gamesPerTeam) || null; // only meaningful for 3+ teams
  const playersPerTeam = options.playersPerTeam ?? null;
  const shuffleTeams = options.shuffleTeams !== false;

  // Grouping — only meaningful for 3+ teams.
  const useGrouping = !!options.useGrouping;
  const numberOfGroups = Number(options.numberOfGroups) || 2;
  const groupMode = options.groupMode === 'across' ? 'across' : 'within'; // default: play within your own group

  if (numberOfRounds < 1) throw new Error('Number of rounds must be at least 1');
  if (gamesPerRound < 1) throw new Error('Games per round must be at least 1');
  if (useGrouping && numberOfGroups < 2) throw new Error('Number of groups must be at least 2');

  const teams = shuffleTeams ? shuffleArray([...participants]) : [...participants];
  const groupAssignment = useGrouping ? assignGroups(teams, numberOfGroups, options.groupAssignments) : null;
  const totalLeagueMatches = numberOfRounds * gamesPerRound;
  const { rounds: roundPairings, hasRoundConflicts, maxSimultaneous } = buildRounds(
    teams,
    numberOfRounds,
    gamesPerRound,
    gamesPerTeam,
    groupAssignment,
    useGrouping ? groupMode : null,
    teamCapacity
  );
  const warnings = hasRoundConflicts
    ? [teamCapacity > 1
        ? `With ${teams.length} teams (each fielding up to ${teamCapacity} simultaneous pairs), at most ${maxSimultaneous} games can run per round — some rounds need a team to field more pairs than its roster supports.`
        : `With ${teams.length} teams, at most ${maxSimultaneous} games can run per round without a team playing twice — some rounds have a team scheduled for more than one match.`]
    : [];

  const rounds = [];
  const matches = [];
  let matchNumber = 1;

  for (let r = 1; r <= numberOfRounds; r++) {
    const roundMatches = [];
    for (let g = 0; g < gamesPerRound; g++) {
      const { teamA, teamB } = roundPairings[r - 1][g];
      const match = {
        id: generateMatchId(),
        matchNumber: matchNumber++,
        roundNumber: r,
        roundName: `Round ${r}`,
        roundType: 'league',
        teamA,
        teamB,
        isBye: false,
        winner: null,
        teamAScore: null,
        teamBScore: null,
        status: 'pending',
        scheduledAt: null,
        venue: null,
      };
      roundMatches.push(match);
      matches.push(match);
    }
    rounds.push({
      roundNumber: r,
      roundName: `Round ${r}`,
      roundType: 'league',
      matches: roundMatches,
    });
  }

  // Playoffs — teams TBD, resolved later from standings.
  if (numberOfPlayoffGames > 0) {
    const playoffMatches = [];
    for (let p = 1; p <= numberOfPlayoffGames; p++) {
      const match = {
        id: generateMatchId(),
        matchNumber: matchNumber++,
        roundNumber: numberOfRounds + 1,
        roundName: numberOfPlayoffGames === 1 ? 'Final' : `Playoff Game ${p}`,
        roundType: 'playoff',
        teamA: null,
        teamB: null,
        winner: null,
        status: 'pending',
        scheduledAt: null,
        venue: null,
      };
      playoffMatches.push(match);
      matches.push(match);
    }
    rounds.push({
      roundNumber: numberOfRounds + 1,
      roundName: 'Playoffs',
      roundType: 'playoff',
      matches: playoffMatches,
    });
  }

  return {
    format: 'custom',
    formatName: 'Custom Format',
    numberOfRounds,
    gamesPerRound,
    numberOfPlayoffGames,
    gamesPerTeam,
    playersPerTeam,
    useGrouping,
    numberOfGroups: useGrouping ? numberOfGroups : null,
    groupMode: useGrouping ? groupMode : null,
    groups: useGrouping
      ? Array.from({ length: numberOfGroups }, (_, g) =>
          teams.filter((_, idx) => groupAssignment[idx] === g)
        )
      : null,
    rounds,
    matches,
    participants: teams,
    totalMatches: matches.length,
    warnings,
  };
};

const partnerKey = (a, b) => [a, b].sort().join('|');

/**
 * Builds this team's pairs for one round: `gamesNeeded` disjoint (no player
 * repeated within the round) pairs, greedily choosing whichever *partnership*
 * (specific pair of players) has teamed up the fewest times so far this
 * schedule — not just whichever individual players have played the fewest
 * games. Ties broken randomly for variety. Mirrors the same least-used-pairing
 * approach buildRounds already uses for team-vs-team matchups, one level down.
 *
 * If the roster is too small/odd to keep filling fresh disjoint pairs (e.g.
 * an odd roster with one player always left over), falls back to reusing the
 * full roster for whatever pairs are left rather than returning fewer pairs
 * than requested.
 */
const buildPairsForRound = (partnershipUsage, roster, gamesNeeded) => {
  let available = [...roster];
  const pairs = [];

  for (let g = 0; g < gamesNeeded; g++) {
    if (available.length < 2) available = [...roster]; // ran out of fresh players — reuse rather than shortchange the round

    let candidates = [];
    for (let i = 0; i < available.length; i++) {
      for (let j = i + 1; j < available.length; j++) {
        candidates.push([available[i], available[j]]);
      }
    }
    const usage = ([p1, p2]) => partnershipUsage.get(partnerKey(p1.id, p2.id)) || 0;
    candidates = shuffleArray(candidates).sort((a, b) => usage(a) - usage(b));
    const [p1, p2] = candidates[0];

    partnershipUsage.set(partnerKey(p1.id, p2.id), usage([p1, p2]) + 1);
    pairs.push([p1, p2]);
    available = available.filter(p => p.id !== p1.id && p.id !== p2.id);
  }

  return pairs;
};

/**
 * Auto-assigns which players from each team's roster play each match, for
 * formats where a team's roster (up to 20) exceeds what actually takes the
 * court in one game (e.g. 2 for doubles pickleball) — the same subset
 * GameCard's manual "Playing" picker sets, just chosen automatically here.
 *
 * For doubles (requiredPlayerCount === 2), rotates *partnerships* too, not
 * just individual playing time — a team fielding multiple simultaneous games
 * in one round (its roster supports more than one pair) gets a fresh,
 * non-repeating pairing each round wherever possible, so players get
 * different partners over the course of the schedule instead of the same
 * partner every single game. For singles (requiredPlayerCount === 1, no
 * partnership concept) or anything else, falls back to picking whichever
 * individual players have played the fewest games so far.
 *
 * `rosterByTeamId` — object of teamId -> array of {id, name} players.
 * Only matches where a side is a team with more roster members than
 * `requiredPlayerCount` get a player list; anything else (individual
 * participants, teams already at/under the required count, or a missing
 * roster entry) is left untouched.
 */
export const assignPlayersToRounds = (rounds, rosterByTeamId, requiredPlayerCount) => {
  if (!requiredPlayerCount) return rounds;

  if (requiredPlayerCount !== 2) {
    // No partnership concept below 2 (singles) or above (not used today) —
    // just rotate individual playing time.
    const usageCounts = new Map();
    const pickPlayers = (participant) => {
      if (participant?.type !== 'team') return null;
      const roster = rosterByTeamId?.[participant.id];
      if (!Array.isArray(roster) || roster.length <= requiredPlayerCount) return null;

      const byUsage = new Map();
      roster.forEach(p => {
        const count = usageCounts.get(p.id) || 0;
        if (!byUsage.has(count)) byUsage.set(count, []);
        byUsage.get(count).push(p);
      });
      const ordered = [...byUsage.keys()]
        .sort((a, b) => a - b)
        .flatMap(count => shuffleArray(byUsage.get(count)));

      const picked = ordered.slice(0, requiredPlayerCount);
      picked.forEach(p => usageCounts.set(p.id, (usageCounts.get(p.id) || 0) + 1));
      return picked;
    };

    return rounds.map(round => ({
      ...round,
      matches: round.matches.map(match => {
        if (!match.teamA || !match.teamB) return match;
        const participantAPlayers = pickPlayers(match.teamA);
        const participantBPlayers = pickPlayers(match.teamB);
        return {
          ...match,
          ...(participantAPlayers ? { participantAPlayers } : {}),
          ...(participantBPlayers ? { participantBPlayers } : {}),
        };
      }),
    }));
  }

  // Doubles: track each team's partnership history across the whole
  // schedule (teamId -> Map(partnerKey -> times paired)).
  const partnershipUsageByTeam = new Map();
  const getPartnershipUsage = (teamId) => {
    if (!partnershipUsageByTeam.has(teamId)) partnershipUsageByTeam.set(teamId, new Map());
    return partnershipUsageByTeam.get(teamId);
  };

  return rounds.map(round => {
    // A team fielding several simultaneous games this round needs all of its
    // pairs built together (so they're disjoint) before handing them out
    // match by match — count how many games each team plays in this round.
    const gamesThisRoundByTeam = new Map();
    round.matches.forEach(match => {
      [match.teamA, match.teamB].forEach(p => {
        if (p?.type === 'team') gamesThisRoundByTeam.set(p.id, (gamesThisRoundByTeam.get(p.id) || 0) + 1);
      });
    });

    const pairPoolByTeam = new Map();
    gamesThisRoundByTeam.forEach((gamesNeeded, teamId) => {
      const roster = rosterByTeamId?.[teamId];
      if (Array.isArray(roster) && roster.length > requiredPlayerCount) {
        pairPoolByTeam.set(teamId, buildPairsForRound(getPartnershipUsage(teamId), roster, gamesNeeded));
      }
    });

    const nextIndexByTeam = new Map();
    const nextPairFor = (participant) => {
      if (participant?.type !== 'team') return null;
      const pool = pairPoolByTeam.get(participant.id);
      if (!pool) return null;
      const idx = nextIndexByTeam.get(participant.id) || 0;
      nextIndexByTeam.set(participant.id, idx + 1);
      return pool[idx] || null;
    };

    return {
      ...round,
      matches: round.matches.map(match => {
        if (!match.teamA || !match.teamB) return match; // playoff placeholders — teams TBD
        const participantAPlayers = nextPairFor(match.teamA);
        const participantBPlayers = nextPairFor(match.teamB);
        return {
          ...match,
          ...(participantAPlayers ? { participantAPlayers } : {}),
          ...(participantBPlayers ? { participantBPlayers } : {}),
        };
      }),
    };
  });
};

/**
 * Format description card for the "Choose Tournament Format" UI — merged
 * into TOURNAMENT_FORMATS at the import site in ScheduleTab.jsx.
 */
export const CUSTOM_FORMAT = {
  id: 'custom',
  name: 'Custom Format',
  shortName: 'Custom',
  description: 'Define your own number of rounds, games per round, and playoff games — a full schedule skeleton is generated automatically.',
  minTeams: 2,
  icon: 'settings',
  recommendedFor: "Tournaments that don't fit a standard format",
};

export default generateCustomFormat;
